/**
 * `GET /ai/insights/{id}/evidence` — the click from a sentence to the number behind it.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  IT IS A SENSITIVE READ, SO IT IS BEHIND THE SHARED REASON GATE
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `x-wp-sensitive-access: true`, `X-Access-Reason` mandatory at ten characters, and
 * the read writes a `wp_data_access_logs` row whose `record_count` is the rows
 * actually handed over and whose `target_employee_ids` are the people actually read
 * (SEC-35). Missing reason is `403 WORKFORCE_ACCESS_REASON_REQUIRED` and **no log row
 * is written**.
 *
 * `AccessReasonGate` is the shared component from Task 53 rather than a fourth copy,
 * for `docs/UI_PROCESS.md` §2's reason — including its Vietnamese-in-a-header refusal
 * (G20), which is still an open server-side gap and still fails closed here.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  AN ITEM THE CALLER MAY NOT READ KEEPS ITS PLACE AND CARRIES NO VALUE
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * The contract is explicit that `value` is **omitted**, not `null`: "một khoá mang
 * `null` bị renderer đọc là 'đã đo, và kết quả là không có gì'". `ai/evidence.ts`
 * keeps that in the type — the withheld variant has no `value` member at all — so
 * this component cannot print an empty object, a dash or a zero for a refused row
 * even by mistake.
 *
 * Fabricated, deleted and out-of-scope are three causes with one result and the
 * caller is not told which. That is said on the panel, because an investigator who
 * assumes "not accessible" means "somebody deleted it" has drawn a conclusion the
 * server deliberately did not offer.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  AND THE SCORE'S FRESHNESS IS RENDERED HERE, BY THE SHARED COMPONENT
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * G16 put `freshness` on this endpoint's `value` for every `productivity_score` item,
 * with the three fields §8.6 publishes. It is a **live** read, so it says *now*; the
 * insight's own `limitations` say what was true at generation. Two different
 * questions, and the panel says so rather than letting a reader merge them.
 *
 * `FreshnessNote` is Task 53's component, reused rather than reimplemented. That is
 * what keeps `scheduled` and `requires_request` four channels apart on this screen
 * too — a second implementation here is exactly where the two would collapse back
 * into one grey "đang cập nhật", and the person told to wait would be waiting for a
 * sweep that will never reach their period.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { accessHeaders } from '../../access/reason';
import { AccessReasonGate } from '../../access/AccessReasonGate';
import { aiEvidencePath } from '../../api/paths';
import type { AiEvidenceResponse } from '../../api/types';
import { useApiQuery } from '../../api/useApiQuery';
import { readAiRefusal } from '../../ai/refusal';
import {
  accessibleCount,
  evidenceTarget,
  readEvidenceItem,
  type EvidenceReading,
} from '../../ai/evidence';
import { FreshnessNote } from '../myactivity/FreshnessNote';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';
import { RefusalPanel } from './RefusalPanel';

/**
 * One aggregate field, as text.
 *
 * `String(entry)` on an `unknown` is `[object Object]` for anything structured, which
 * ESLint's `no-base-to-string` is right about: on this panel that string would sit
 * where a person expects the number a claim rests on. Money and scores arrive as
 * **strings** (`numeric` rendered by PostgreSQL) and are printed verbatim — parsing
 * them into JavaScript doubles here would be this client re-rendering somebody's
 * score with its own arithmetic.
 */
function renderScalar(entry: unknown): string {
  if (typeof entry === 'string') return entry;
  if (typeof entry === 'number' || typeof entry === 'boolean') return String(entry);
  // Written out rather than left to `JSON.stringify`, whose declared return type is
  // `string` while it really answers `undefined` for `undefined` — a lie the compiler
  // repeats and `no-unnecessary-condition` then calls the guard dead code.
  if (entry === null) return 'null';
  if (entry === undefined) return 'undefined';
  return JSON.stringify(entry);
}

function ValueRows({ value }: { readonly value: Readonly<Record<string, unknown>> }): ReactNode {
  // `freshness` is rendered by its own component below rather than as a row of
  // stringified JSON, which is why it is dropped here: printing it twice, once as
  // `[object Object]`, would be worse than not printing it at all.
  const entries = Object.entries(value).filter(([key]) => key !== 'freshness');
  return (
    <dl className="wp-evidence-values">
      {entries.map(([key, entry]) => (
        <div key={key}>
          <dt className="wp-small wp-muted">{key}</dt>
          <dd className="wp-mono wp-num">{renderScalar(entry)}</dd>
        </div>
      ))}
    </dl>
  );
}

function EvidenceItem({ reading }: { readonly reading: EvidenceReading }): ReactNode {
  const { t } = useI18n();
  if (reading.kind === 'malformed') {
    return (
      <tr>
        <td className="wp-small" colSpan={3} data-testid="m4-evidence-malformed">
          {t('m4.evidence.malformed')}
        </td>
      </tr>
    );
  }

  const target = evidenceTarget(reading.refKind, reading.ref);
  const head = (
    <th scope="row" className="wp-mono" data-testid={`m4-evidence-head-${reading.evidenceId}`}>
      {reading.evidenceId} · {reading.refKind} · {reading.ref}
    </th>
  );

  const targetLine =
    target.kind === 'reachable' ? (
      <p className="wp-small" data-testid={`m4-evidence-target-${reading.evidenceId}`}>
        {t('m4.evidence.target.reachable')}
      </p>
    ) : (
      <p className="wp-small" data-testid={`m4-evidence-gap-${reading.evidenceId}`}>
        {t(
          target.kind === 'noEndpoint'
            ? 'm4.evidence.target.noEndpoint'
            : 'm4.evidence.target.notEmitted',
          { needed: target.needed },
        )}
      </p>
    );

  if (reading.kind === 'withheld') {
    return (
      <tr data-testid={`m4-evidence-item-${reading.evidenceId}`} data-evidence-kind="withheld">
        {head}
        <td>
          {/* No value cell, and specifically not a dash: the contract OMITS `value`
              on a row the caller may not read, and a placeholder in this column is
              the "measured, and the result was nothing" the omission exists to
              prevent. The sentence takes the cell instead. */}
          <p
            className="wp-small"
            data-testid={
              reading.reason === 'not_accessible'
                ? `m4-evidence-withheld-${reading.evidenceId}`
                : `m4-evidence-novalue-${reading.evidenceId}`
            }
          >
            {t(
              reading.reason === 'not_accessible'
                ? 'm4.evidence.withheld.not_accessible'
                : 'm4.evidence.withheld.no_value_published',
            )}
          </p>
        </td>
        <td>{targetLine}</td>
      </tr>
    );
  }

  return (
    <tr data-testid={`m4-evidence-item-${reading.evidenceId}`} data-evidence-kind="accessible">
      {head}
      <td>
        <ValueRows value={reading.value} />
        {reading.refKind === 'productivity_score' ? (
          <div data-testid={`m4-evidence-freshness-${reading.evidenceId}`}>
            <p className="wp-small wp-muted">{t('m4.evidence.freshness.title')}</p>
            <p className="wp-small" data-testid={`m4-evidence-freshness-now-${reading.evidenceId}`}>
              {t('m4.evidence.freshness.body')}
            </p>
            {/* The shared component. One implementation of the `scheduled` /
                `requires_request` distinction across this whole build. */}
            <FreshnessNote freshness={reading.freshness} />
          </div>
        ) : null}
      </td>
      <td>{targetLine}</td>
    </tr>
  );
}

export interface EvidencePanelProps {
  readonly insightId: string;
  readonly reason: string;
  readonly onReason: (reason: string) => void;
}

export function EvidencePanel({ insightId, reason, onReason }: EvidencePanelProps): ReactNode {
  const { t } = useI18n();
  const headers = accessHeaders(reason);
  // `path: null` is the hook's own "there is nothing to ask yet". No request is
  // issued until a usable reason exists — a screen that fired and then rendered the
  // 403 would teach people that refusals are background noise.
  const path = headers === undefined ? null : aiEvidencePath(insightId);
  const query = useApiQuery<AiEvidenceResponse>(path, 0, headers === undefined ? {} : { headers });

  const readings = (query.data?.evidence ?? []).map(readEvidenceItem);

  return (
    <section className="wp-section" aria-labelledby="m4-evidence-heading" data-testid="m4-evidence">
      <h2 className="wp-section__title" id="m4-evidence-heading" tabIndex={-1}>
        {t('m4.evidence.title', { id: insightId })}
      </h2>
      <p className="wp-small">{t('m4.evidence.body')}</p>
      <p className="wp-small wp-muted" data-testid="m4-evidence-scope-note">
        {t('m4.evidence.scopeNote')}
      </p>

      <AccessReasonGate
        value={reason}
        onOpen={onReason}
        subject={t('m4.subject.evidence')}
        testId="m4-evidence-gate"
      />

      {path === null ? null : query.status === 'loading' ? (
        <Skeleton rows={3} label={t('m4.subject.evidence')} testId="m4-evidence-skeleton" />
      ) : query.status === 'error' && query.error !== null ? (
        <RefusalPanel
          refusal={readAiRefusal(query.error)}
          error={query.error}
          subject={t('m4.subject.evidence')}
          onRetry={query.reload}
        />
      ) : readings.length === 0 ? (
        <StatusNote severity="info" title={t('m4.evidence.empty')} testId="m4-evidence-empty" />
      ) : (
        <>
          <p className="wp-small" data-testid="m4-evidence-count">
            {t('m4.evidence.count', {
              shown: accessibleCount(readings),
              total: readings.length,
            })}
          </p>
          <p className="wp-small wp-muted" data-testid="m4-evidence-three-causes">
            {t('m4.evidence.threeCauses')}
          </p>
          {/* A table, because every row answers the same three questions — which
              reference, what it holds, where the number can be re-read — and a list of
              paragraphs makes a reader compare them by scrolling. */}
          <div className="wp-table-scroll">
            <table className="wp-table wp-table--fluid wp-evidence-table" data-testid="m4-evidence-table">
              <caption className="wp-visually-hidden">
                {t('m4.evidence.title', { id: insightId })}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{t('m4.evidence.col.ref')}</th>
                  <th scope="col">{t('m4.evidence.col.value')}</th>
                  <th scope="col">{t('m4.evidence.col.where')}</th>
                </tr>
              </thead>
              <tbody>
                {readings.map((reading, index) => (
                  <EvidenceItem
                    key={
                      reading.kind === 'malformed'
                        ? `malformed-${String(index)}`
                        : reading.evidenceId
                    }
                    reading={reading}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
